import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Calendar, 
  Newspaper, 
  Users, 
  Trophy, 
  Mail,
  LogOut,
  Menu,
  Tags,
  Heart,
  ShoppingBag,
  Package,
  UserCog,
  Briefcase,
  FileText,
  Image,
  CalendarDays,
  UsersRound,
  MapPin,
  Send,
  ClipboardList,
  Shield,
  ChevronDown,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useState, useEffect } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_kickoff-central-10/artifacts/72zh29ie_Middel%2020%40300x.png";

// Grouped navigation structure
const navigationGroups = [
  {
    title: "Club Beheer",
    items: [
      { label: "Dashboard", path: "/admin", icon: LayoutDashboard, roles: ["super_admin", "admin", "editor", "viewer"] },
      { label: "Wedstrijden", path: "/admin/matches", icon: Calendar, roles: ["super_admin", "admin", "editor"] },
      { label: "Teams", path: "/admin/teams", icon: Users, roles: ["super_admin", "admin", "editor"] },
      { label: "Spelers", path: "/admin/players", icon: Users, roles: ["super_admin", "admin", "editor"] },
      { label: "Staff", path: "/admin/staff", icon: Shield, roles: ["super_admin", "admin", "editor"] },
      { label: "Speler Inschrijvingen", path: "/admin/player-registrations", icon: ClipboardList, roles: ["super_admin", "admin", "editor"] },
      { label: "Ranglijst", path: "/admin/standings", icon: Trophy, roles: ["super_admin", "admin", "editor"] },
      { label: "Competities", path: "/admin/competitions", icon: Trophy, roles: ["super_admin", "admin", "editor"] },
      { label: "Locaties", path: "/admin/locations", icon: MapPin, roles: ["super_admin", "admin", "editor"] },
      { label: "Categorieën", path: "/admin/categories", icon: Tags, roles: ["super_admin", "admin"] },
    ]
  },
  {
    title: "Content & Media",
    items: [
      { label: "Nieuws", path: "/admin/news", icon: Newspaper, roles: ["super_admin", "admin", "editor"] },
      { label: "Vacatures", path: "/admin/vacancies", icon: Briefcase, roles: ["super_admin", "admin", "editor"] },
      { label: "Sollicitaties", path: "/admin/applications", icon: FileText, roles: ["super_admin", "admin", "editor"] },
      { label: "Sponsors", path: "/admin/sponsors", icon: Heart, roles: ["super_admin", "admin", "editor"] },
    ]
  },
  {
    title: "Sport Eldorado",
    items: [
      { label: "SE Galerij", path: "/admin/gallery", icon: Image, roles: ["super_admin", "admin", "editor", "sport_eldorado_admin"] },
      { label: "SE Activiteiten", path: "/admin/activities", icon: CalendarDays, roles: ["super_admin", "admin", "editor", "sport_eldorado_admin"] },
      { label: "SE Team", path: "/admin/eldorado-team", icon: UsersRound, roles: ["super_admin", "admin", "editor", "sport_eldorado_admin"] },
      { label: "SE Inschrijvingen", path: "/admin/registrations", icon: ClipboardList, roles: ["super_admin", "admin", "sport_eldorado_admin"] },
    ]
  },
  {
    title: "Webshop",
    items: [
      { label: "Producten", path: "/admin/shop", icon: ShoppingBag, roles: ["super_admin", "admin", "editor"] },
      { label: "Bestellingen", path: "/admin/orders", icon: Package, roles: ["super_admin", "admin", "editor"] },
    ]
  },
  {
    title: "Systeem",
    items: [
      { label: "Nieuwsbrief", path: "/admin/newsletter", icon: Send, roles: ["super_admin", "admin"] },
      { label: "Contacten", path: "/admin/contacts", icon: Mail, roles: ["super_admin", "admin"] },
      { label: "Gebruikers", path: "/admin/users", icon: UserCog, roles: ["super_admin"] },
    ]
  },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userRole, setUserRole] = useState("viewer");
  const [username, setUsername] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    const role = localStorage.getItem("adminRole") || "viewer";
    const user = localStorage.getItem("adminUser") || "";
    setUserRole(role);
    setUsername(user);
    
    // Expand all groups by default
    const initialExpanded = {};
    navigationGroups.forEach(group => {
      initialExpanded[group.title] = true;
    });
    setExpandedGroups(initialExpanded);
    
    // Apply admin-theme to body for portal components (dialogs, selects, etc.)
    document.body.classList.add('admin-theme');
    
    return () => {
      document.body.classList.remove('admin-theme');
    };
  }, []);

  const isActive = (path) => {
    if (path === "/admin") return location.pathname === "/admin";
    // Exact match for paths that could conflict (e.g., /admin/news vs /admin/newsletter)
    if (path === "/admin/news") return location.pathname === "/admin/news";
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    localStorage.removeItem("adminRole");
    localStorage.removeItem("adminEmail");
    navigate("/admin/login");
  };

  const toggleGroup = (title) => {
    setExpandedGroups(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  const filterGroupItems = (items) => {
    return items.filter(item => item.roles.includes(userRole));
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-white">
      {/* Logo Header */}
      <div className="p-5 border-b border-slate-200">
        <Link to="/" className="flex items-center gap-3 group">
          <img src={LOGO_URL} alt="Futsal Gent" className="h-10 w-auto" />
          <div>
            <span className="font-black text-slate-900 font-['Chivo'] block tracking-tight">FUTSAL GENT</span>
            <span className="text-[11px] text-slate-500 font-['Manrope'] font-medium">Admin Panel</span>
          </div>
        </Link>
      </div>

      {/* User Info */}
      <div className="px-5 py-4 bg-gradient-to-r from-blue-50 to-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center">
            <span className="text-white font-semibold text-sm font-['Manrope']">
              {username ? username.charAt(0).toUpperCase() : "A"}
            </span>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900 font-['Manrope']">{username || "Admin"}</p>
            <p className="text-xs text-blue-600 font-medium capitalize font-['Manrope']">
              {userRole.replace("_", " ")}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {navigationGroups.map((group) => {
          const filteredItems = filterGroupItems(group.items);
          if (filteredItems.length === 0) return null;
          
          return (
            <div key={group.title} className="mb-3">
              <button
                onClick={() => toggleGroup(group.title)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider font-['Manrope'] hover:text-slate-700 transition-colors"
              >
                <span>{group.title}</span>
                <ChevronDown 
                  className={`h-4 w-4 transition-transform duration-200 ${
                    expandedGroups[group.title] ? 'rotate-180' : ''
                  }`} 
                />
              </button>
              
              {expandedGroups[group.title] && (
                <div className="mt-1 space-y-0.5">
                  {filteredItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 ${
                          active
                            ? "bg-blue-600 text-white shadow-sm"
                            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                        }`}
                        data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Icon className={`h-[18px] w-[18px] ${active ? 'text-white' : 'text-slate-400'}`} />
                        <span className="font-medium text-[13px] font-['Manrope']">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer Actions */}
      <div className="p-4 border-t border-slate-200 space-y-2">
        <Link 
          to="/" 
          target="_blank"
          className="flex items-center gap-2 px-3 py-2.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-['Manrope'] text-sm"
        >
          <ExternalLink className="h-4 w-4" />
          <span>Bekijk website</span>
        </Link>
        <Button
          variant="ghost"
          className="w-full justify-start text-slate-500 hover:text-red-600 hover:bg-red-50 font-['Manrope']"
          onClick={handleLogout}
          data-testid="admin-logout"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Uitloggen
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 admin-theme">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 h-16 flex items-center px-4 shadow-sm">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-slate-700" data-testid="admin-mobile-menu">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-[280px]">
            <SidebarContent />
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-2 ml-4">
          <img src={LOGO_URL} alt="Futsal Gent" className="h-8 w-auto" />
          <span className="font-black text-slate-900 font-['Chivo']">Admin</span>
        </div>
      </header>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-slate-200 shadow-sm">
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
